import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Grid } from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import * as yup from 'yup';
import { FormTextField } from '../../../common/form';
import { OrganizationDto } from '../../../common/PricePrgClient';
import { ProgressButton } from '../../../common/ProgressButton';
import { RootState } from '../../../store';
import { getOrganization, getSettingsUi } from '../redux/settingsSelectors';
import { updateOrganization } from '../redux/settingsSlices';

const mapStateToProps = (state: RootState) => ({
    organization: getOrganization(state),
    isSaving: getSettingsUi(state).isSavingOrganization
});
type Props = ReturnType<typeof mapStateToProps>;

const schema: yup.SchemaOf<OrganizationDto> = yup.object().shape({
    name: yup.string().optional().max(60).label('Name'),
    address: yup.string().optional().max(100).label('Address'),
    zipCode: yup.string().optional().max(10).label('Zip code'),
    city: yup.string().optional().max(35).label('City'),
    phone: yup.string().optional().max(15).label('Phone'),
    email: yup.string().optional().email().label('Email'),
    logoUrl: yup.mixed().optional(),
    country: yup.string().optional().max(60).label('Country')
});

const fields = Object.entries(schema.fields)
    .filter(([key]) => key !== 'logoUrl')
    .map(([key, value]) => ({
        name: key as keyof OrganizationDto,
        label: value.spec.label
    }));

const OrganizationSettingsTab = ({ organization, isSaving }: Props) => {
    const [logoUrl, setLogoUrl] = useState<string | null>();

    const dispatch = useDispatch();

    const { register, control, handleSubmit } = useForm<OrganizationDto & { logo: Blob }>({
        defaultValues: useMemo(
            () => ({
                name: organization.name ?? '',
                address: organization.address ?? '',
                zipCode: organization.zipCode ?? '',
                city: organization.city ?? '',
                phone: organization.phone ?? '',
                email: organization.email ?? '',
                country: organization.country ?? ''
            }),
            [organization]
        ),
        resolver: yupResolver(schema)
    });

    useEffect(() => {
        if (organization.logoUrl != null) {
            // Add query param to prevent caching if picture should be fetched from server
            const suffix = organization.logoUrl.startsWith('blob:') ? '' : `?${new Date().getTime()}`;
            setLogoUrl(organization.logoUrl + suffix);
        }
    }, [organization]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files != null && files.length > 0) {
            setLogoUrl(URL.createObjectURL(files[0]));
        }
    };

    const onSubmit = (dto: OrganizationDto) => {
        dispatch(updateOrganization.request({ ...dto, logoUrl: logoUrl ?? undefined }));
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} direction="column">
                {fields.map(field => (
                    <Grid item xs={12} lg={6} key={field.name}>
                        <FormTextField control={control} name={field.name} label={field.label} fullWidth />
                    </Grid>
                ))}
                {logoUrl != null && (
                    <Grid item>
                        <Box component="img" sx={{ maxHeight: '200px' }} src={logoUrl} alt="logo url" />
                    </Grid>
                )}
                <Grid item>
                    <Grid container direction="column" spacing={2}>
                        <Grid item>
                            <Box
                                component="input"
                                {...register('logo')}
                                id="logo"
                                type="file"
                                accept="image/png"
                                onChange={handleFileChange}
                                sx={{ display: 'none' }}
                            />
                        </Grid>
                        <Grid item>
                            <label htmlFor="logo">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<PhotoCamera />}
                                    component="span"
                                >
                                    Select logo
                                </Button>
                            </label>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item>
                    <ProgressButton type="submit" variant="contained" color="primary" isLoading={isSaving}>
                        Save
                    </ProgressButton>
                </Grid>
            </Grid>
        </form>
    );
};

export default connect(mapStateToProps)(OrganizationSettingsTab);
